import { observable, makeObservable, computed, runInAction } from 'mobx'
import humps from 'humps'
import { observer } from 'mobx-react'
import React from 'react'
import { Component } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { RouteComponentProps } from 'react-router-dom'
import { Colors } from 'src/constants/colors'
import { Asset } from 'src/models/Asset'
import { FeedPost } from 'src/models/FeedPost'
import { Prompt } from 'src/models/Prompt'
import { LibraryCategory } from 'src/pages/Library'
import { sharedAppStateStore } from 'src/store/AppStateStore'
import { sharedDataStore, ViewMode } from 'src/store/DataStore'
import { fetchContent, getFeedContentType, getFieldsParam, LearningContent } from 'src/utils/content'
import { getQueryParam, getQueryParams } from 'src/utils/urlParams'
import {
  ContainerDiv,
  FjText,
  FeedEmptyContentConnectSalesforce,
  FeedEmptyContentConnectGong,
  Loader,
  FeedEmptyContent,
  FeedEmptyContentProps,
} from 'src/components/Common'
import { LibraryCard } from 'src/components/Library/LibraryCard'
import { FjFilter, FjFilterProps, SortBy } from 'src/components/Common/FjFilter'
import styled from 'styled-components'
import { Template } from 'src/models/Template'
import { LibraryTableView } from 'src/components/Library/LibraryTableView'
import { CustomMetadataFieldOptions } from 'src/models/Config'

export const LibraryGridContainer = styled.div<{ viewMode: ViewMode; singleRow?: boolean }>`
  display: grid;
  grid-template-columns: ${(props) => (props.viewMode === 'card' ? 'repeat(5, 1fr)' : '100%')};
  grid-gap: 10px;
  @media (max-width: 1200px) {
    grid-template-columns: ${(props) => (props.viewMode === 'card' ? 'repeat(4, 1fr)' : '100%')};
    & > :nth-child(n + 5) {
      display: ${(props) => (props.singleRow ? 'none' : undefined)};
    }
  }
  @media (max-width: 992px) {
    grid-template-columns: ${(props) => (props.viewMode === 'card' ? 'repeat(3, 1fr)' : '100%')};
    & > :nth-child(n + 4) {
      display: ${(props) => (props.singleRow ? 'none' : undefined)};
    }
  }
  @media (max-width: 767px) {
    grid-template-columns: ${(props) => (props.viewMode === 'card' ? 'repeat(2, 1fr)' : '100%')};
    & > :nth-child(n + 3) {
      display: ${(props) => (props.singleRow ? 'none' : undefined)};
    }
  }
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`

const HorizontalCardContainer = styled.div`
  width: 20%;
  min-width: 20%;
  padding: 5px;
  @media (max-width: 1200px) {
    width: 25%;
    min-width: 25%;
  }
  @media (max-width: 992px) {
    width: 33.33%;
    min-width: 33.33%;
  }
  @media (max-width: 767px) {
    width: 50%;
    min-width: 50%;
  }
  @media (max-width: 576px) {
    width: 100%;
    min-width: 100%;
  }
`

export interface breadcrumbContextType {
  breadcrumbName?: string
  contentType?: 'hub' | 'learningpath' | 'course'
}

interface LibraryGridProps
  extends RouteComponentProps,
    Omit<FjFilterProps, 'category' | 'handleSetSortBy' | 'sortBy' | 'onFiltersChange'> {
  apiQueryParams?: object
  gridHeight?: string
  customEndpoint?: string
  customTitle?: React.ReactElement
  horizontalScroll?: boolean
  emptyContentProps?: FeedEmptyContentProps
  breadcrumbContextObj?: breadcrumbContextType
  searchType?: 'fullsearch' | 'aisearch'
  handleNavigation?: (params?: any) => void
  handlePromotedSearchChangeSuccess?: () => Promise<void>
  contentLoaded?: (loaded: boolean, queryParams: object) => void
  useCache?: boolean
}

@observer
export class LibraryGrid extends Component<LibraryGridProps> {
  static defaultProps: Partial<LibraryGridProps> = {
    showFilter: true,
    showSort: true,
    showViewSwitcher: true,
    excludeSearchParams: ['contentType', 'q', 'searchType'],
  }

  pageNum = 1
  totalItems = 0
  @observable isLoading = true
  @observable tablePageLoading = false
  @observable hasMore = false
  @observable content: LearningContent[] = []
  @observable sortBy: SortBy = 'recent'
  @observable queryParams: any = {}
  @observable contentType: LibraryCategory
  @observable categories: LibraryCategory[] = [
    'feedpost',
    'asset',
    'playlist',
    'course',
    'learningpath',
    'prompt',
    'managerPicks',
    'opportunity',
    'call',
    'hub',
    'fullsearch',
    'aisearch',
    'dealroom',
  ]
  // `filtersCustomFields` is used to set the `overrideCustomMetadataFieldOptions` prop of `FjFilter` component, specifically for the Search page.
  @observable filtersCustomFields: CustomMetadataFieldOptions
  abortController: AbortController

  @computed get fjFilterProps(): FjFilterProps & RouteComponentProps {
    return {
      category: this.contentType,
      handleSetSortBy: this.handleSetSortBy,
      sortBy: this.sortBy,
      onFiltersChange: this.onFiltersChange,
      ...this.props,
      overrideCustomMetadataFieldOptions:
        this.props.overrideCustomMetadataFieldOptions ??
        this.filtersCustomFields ??
        sharedDataStore.config.libraryCustomFields[this.contentType],
    }
  }

  @computed get viewMode() {
    return this.props.viewSwitcherProps?.initialViewMode ?? sharedDataStore.localSettings.viewMode
  }

  constructor(props: LibraryGridProps) {
    super(props)
    makeObservable(this)
  }

  async componentDidMount() {
    sharedDataStore.handleContentCreated = this.handleContentCreated
    this.checkURLAndFetch()
  }

  componentWillUnmount() {
    sharedDataStore.handleContentCreated = undefined
    sharedAppStateStore.bulkEditPopupProps = undefined
  }

  componentDidUpdate(prevProps: LibraryGridProps) {
    if (
      JSON.stringify(prevProps.apiQueryParams) !== JSON.stringify(this.props.apiQueryParams) ||
      this.props.location.search !== prevProps.location.search
    ) {
      this.checkURLAndFetch()
    }
  }

  checkURL = () => {
    const sortBy = getQueryParam('sort_by')
    if (['recent', 'alphabetical', 'popular'].includes(sortBy)) this.sortBy = sortBy as SortBy
    let contentType = getQueryParam('contentType') as LibraryCategory
    this.contentType = this.categories.includes(contentType) ? contentType : undefined
  }

  checkURLAndFetch = () => {
    this.queryParams = getQueryParams(this.props.excludeSearchParams)
    this.checkURL()
    this.pageNum = 1
    this.isFetchAllowed() && this.fetchData()
  }

  handleContentCreated = (obj?: FeedPost | Prompt | Asset) => {
    if (this.contentType === getFeedContentType(obj)) {
      this.pageNum = 1
      this.fetchData()
    }
  }

  isFetchAllowed = () => {
    if (this.contentType === 'opportunity') return sharedDataStore.user.company.hasIntegratedCrmOrganization()
    else if (this.contentType === 'call') return !!sharedDataStore.user.company.integratedCallOrganization
    return true
  }

  onFiltersChange = (params: any) => {
    this.pageNum = 1
    this.queryParams = params
    if (this.props.handleNavigation) {
      this.props.handleNavigation({ ...params, sort_by: this.props.showSort ? this.sortBy : undefined })
    } else {
      this.fetchData()
    }
  }

  handlePageChange = (pageNum: number) => {
    this.pageNum = pageNum
    this.fetchData()
  }

  fetchData = async () => {
    try {
      const { apiQueryParams, customEndpoint, useCache } = this.props
      this.queryParams = {
        ...this.queryParams,
        page: this.pageNum,
        sort_by: this.props.showSort ? this.sortBy : undefined,
        page_size: this.props.horizontalScroll ? 1000 : undefined,
        ...apiQueryParams,
      }

      if (this.props.searchType && (this.queryParams?.search || '').trim() === '') {
        this.content = []
        return
      }

      // Basically, contentType can be blank if there is searchType
      // @todo: HACK to make fullsearch/aisearch work for now. Needs refactor.
      if (!this.contentType && this.props.searchType) {
        this.contentType = this.props.searchType
      }

      let contentType = this.contentType

      if (this.contentType === 'managerPicks') {
        this.queryParams['manager_picks'] = true
      } else if (this.contentType === 'feedpost') {
        this.queryParams['exclude_linked'] = true
      }

      const currentCategory = this.contentType
      const currentQueryParams = this.queryParams
      this.abortController?.abort()
      this.abortController = new AbortController()
      if (this.pageNum === 1) this.setLoading(true)
      else if (this.viewMode === 'table') this.tablePageLoading = true
      const fields = getFieldsParam(contentType)
      const { data, hasNextPage, total, customFields } = await fetchContent(
        contentType,
        { ...this.queryParams, fields },
        customEndpoint,
        this.abortController,
        useCache
      )
      this.totalItems = total
      // Set `filtersCustomFields` only if it's included in the API response. For example, in fullsearch/aisearch, `customFields` is only present in the first page response due to performance optimizations.
      if (customFields) this.filtersCustomFields = humps.decamelizeKeys(customFields) as CustomMetadataFieldOptions
      // This is done to make sure we do not populate the data from a previously pending request, into the current category or filters
      if (
        this.contentType !== currentCategory ||
        JSON.stringify(this.queryParams) !== JSON.stringify(currentQueryParams)
      )
        return

      runInAction(() => {
        this.hasMore = hasNextPage
        this.content = this.pageNum === 1 || this.viewMode === 'table' ? data : [...this.content, ...data]
      })
    } catch (err) {
      if (err.code !== 'ERR_CANCELED') {
        this.content = []
        sharedAppStateStore.handleError(err)
      }
    } finally {
      this.setLoading(false)
      this.tablePageLoading = false
    }
  }

  setLoading = (loading: boolean) => {
    this.isLoading = loading
    if (!loading) this.props.contentLoaded?.(!!this.content.length, this.queryParams)
  }

  fetchMoreData = () => {
    this.pageNum += 1
    this.fetchData()
  }

  onEditSuccess = (obj: LearningContent) => {
    const newContent = [...this.content]
    const index = newContent.findIndex((c) => c.id === obj.id)
    if (index === -1) return
    newContent[index] = obj
    this.content = newContent
  }

  onArchiveSuccess = (obj: LearningContent) => {
    this.content = this.content.filter((p) => p.id !== obj!.id)
  }

  handlePromptDeleted = (prompt: Prompt) => {
    if (!prompt) return
    this.content = [...this.content].filter((c) => c.id !== prompt.id)
  }

  handleSetSortBy = (sortValue: SortBy) => {
    if (this.sortBy !== sortValue) {
      this.sortBy = sortValue
      this.pageNum = 1
      if (this.props.handleNavigation) {
        this.props.handleNavigation({ ...getQueryParams(this.props.excludeSearchParams), sort_by: sortValue })
      } else {
        this.fetchData()
      }
    }
  }

  onDeleteSuccess = (objs: LearningContent | LearningContent[]) => {
    if (Array.isArray(objs)) {
      const idsSet = new Set(objs.map((obj) => obj.id))
      this.content = this.content.filter((p) => !idsSet.has(p.id))
    } else {
      this.content = this.content.filter((p) => p.id !== objs!.id)
    }
  }

  onCreateTemplateSuccess = (template: Template) => {
    const newContent = [...this.content]
    const index = newContent.findIndex((c) => c.id === template.learningContent.id)
    if (index === -1) return
    newContent[index] = template.learningContent
    this.content = newContent
  }

  render() {
    const minHeight = this.props.gridHeight ? `calc(${this.props.gridHeight} - 100px)` : '30vh'
    return (
      <ContainerDiv textAlign="center" borderRadius="20px">
        {this.props.customTitle && (
          <ContainerDiv marginBottom="20px" textAlign="left">
            <FjText textAlign="left" color={Colors.chambray} fontWeight="bold" fontSize="25px">
              {this.props.customTitle}
            </FjText>
          </ContainerDiv>
        )}
        {this.contentType === 'opportunity' && !sharedDataStore.user.company.hasIntegratedCrmOrganization() ? (
          <FeedEmptyContentConnectSalesforce />
        ) : this.contentType === 'call' && !sharedDataStore.user.company.integratedCallOrganization ? (
          <FeedEmptyContentConnectGong />
        ) : (
          <>
            {this.contentType && this.props.showFilter ? (
              <ContainerDiv marginBottom="25px">
                <FjFilter {...this.fjFilterProps} />
              </ContainerDiv>
            ) : null}

            {this.isLoading ? (
              <ContainerDiv minHeight={minHeight} display="flex" alignItems="center" justifyContent="center">
                <Loader />
              </ContainerDiv>
            ) : this.contentType && this.content.length && this.viewMode === 'table' ? (
              <LibraryTableView
                loading={this.tablePageLoading}
                contentType={this.contentType}
                learningContents={this.content as Array<LearningContent>}
                pageNumber={this.pageNum}
                handlePageChange={this.handlePageChange}
                total={this.totalItems}
                hasMore={this.hasMore}
                onEditSuccess={() => this.fetchData()}
                onArchiveSuccess={this.onDeleteSuccess}
                onDeleteSuccess={this.onDeleteSuccess}
              />
            ) : this.content.length ? (
              <>
                {this.props.horizontalScroll ? (
                  <ContainerDiv overflow="auto" display="flex">
                    {this.content &&
                      this.content.map((item) => (
                        <HorizontalCardContainer>
                          <LibraryCard
                            key={item.id}
                            obj={item}
                            viewMode={this.viewMode}
                            handlePromotedSearchChangeSuccess={this.props.handlePromotedSearchChangeSuccess}
                            onEditSuccess={this.onEditSuccess}
                            onArchiveSuccess={() => this.onArchiveSuccess(item)}
                            onDeleteSuccess={() => this.onDeleteSuccess(item)}
                            breadcrumbContextObj={this.props.breadcrumbContextObj}
                            onCreateTemplateSuccess={this.onCreateTemplateSuccess}
                          />
                        </HorizontalCardContainer>
                      ))}
                  </ContainerDiv>
                ) : (
                  <InfiniteScroll
                    dataLength={this.content.length}
                    next={this.fetchMoreData}
                    hasMore={this.hasMore}
                    style={{ overflowY: this.props.gridHeight ? 'auto' : 'hidden', overflowX: 'hidden' }}
                    height={this.props.gridHeight}
                    loader={<Loader />}
                  >
                    <LibraryGridContainer viewMode={this.viewMode}>
                      {this.content &&
                        this.content.map((item) => (
                          <LibraryCard
                            key={item.id}
                            obj={item}
                            viewMode={this.viewMode}
                            handlePromotedSearchChangeSuccess={this.props.handlePromotedSearchChangeSuccess}
                            onEditSuccess={this.onEditSuccess}
                            onArchiveSuccess={() => this.onArchiveSuccess(item)}
                            onDeleteSuccess={() => this.onDeleteSuccess(item)}
                            breadcrumbContextObj={this.props.breadcrumbContextObj}
                            onCreateTemplateSuccess={this.onCreateTemplateSuccess}
                          />
                        ))}
                    </LibraryGridContainer>
                  </InfiniteScroll>
                )}
              </>
            ) : (
              <>
                {this.props.emptyContentProps ? (
                  <FeedEmptyContent {...this.props.emptyContentProps} />
                ) : (
                  <ContainerDiv minHeight={minHeight} display="flex" alignItems="center" justifyContent="center">
                    <FeedEmptyContent title="No data" />
                  </ContainerDiv>
                )}
              </>
            )}
          </>
        )}
      </ContainerDiv>
    )
  }
}
